import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import DynamicCrudPage from '../../../components/DynamicCrudPage';
import { useTeamMembers, useTeamMemberPermissions, SCREENS } from '../../../hooks';
import AddIcon from '@mui/icons-material/Add';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';

const TeamMembersPage = () => {
  const { t } = useTranslation();
  const { teamId } = useParams();
  
  // Custom form fields with user options
  const customFormFields = useCallback((fields, additionalData, isRootUser, t) => {
    const { availableUsers = [] } = additionalData || {};

    return fields.map(field => {
      if (field.name === 'userId') {
        return {
          ...field,
          options: [
            { value: '', label: t('teams.members.selectUser') },
            ...availableUsers.map(user => {
              const baseLabel = `${user.first_name} ${user.last_name} (${user.role})`;
              
              if (user.team_id && user.team) {
                const isManager = user.team.team_manager_id === user.id;
                const roleText = isManager 
                  ? t('teams.members.teamManager') 
                  : t('teams.members.teamMember');
                
                return {
                  value: user.id,
                  label: `${baseLabel} - ${user.team.name} ${roleText}`,
                };
              }
              
              return {
                value: user.id,
                label: `${baseLabel} - ${t('teams.members.noTeam')}`,
              };
            }),
          ],
        };
      }
      return field;
    });
  }, []);

  // Custom filter configs - No filters for team members
  const customFilterConfigs = useCallback(() => {
    return []; // Return empty array to disable filters
  }, []);

  // Custom row actions for team members
  const customRowActions = useCallback((row) => {
    const actions = [
      {
        type: 'remove',
        label: 'table.actions.removeFromTeam',
        color: 'warning',
        icon: <PersonRemoveIcon />,
      },
    ];
    return actions;
  }, [t]);

  // Handle row actions
  const handleRowAction = useCallback((actionType, row) => {
    if (actionType === 'remove') {
      return false; // Triggers handleDeleteClick in DataTableContainer
    }
    return false;
  }, []);

  // Custom hook configuration
  const customHookConfig = useCallback(() => ({
    teamId: parseInt(teamId),
    includeTransferModal: true,
  }), [teamId]);

  // Custom title for team members page
  const customTitle = useCallback(() => {
    return t('pages.teamMembers'); // Will be overridden by hook data
  }, [t]);

  return (
    <DynamicCrudPage
      entityType="teamMembers"
      screen={SCREENS.TEAM_MEMBERS}
      hook={useTeamMembers}
      hookConfig={customHookConfig}
      additionalHooks={{ teamMemberPermissions: useTeamMemberPermissions }}
      customFormFields={customFormFields}
      customFilterConfigs={customFilterConfigs}
      customTitle={customTitle}
      addButtonTextKey="table.actions.addMemberToTeam"
          addButtonIcon={<AddIcon />}
      entityTypeForDataTable="member"
      customRowActions={customRowActions}
          onRowAction={handleRowAction}
      showAddButton={true}
          deleteModalIcon={<PersonRemoveIcon sx={{ color: 'warning.main' }} />}
      customDeleteHandler="removeUserFromTeam"
      deleteConfirmText={t('table.actions.removeFromTeam')}
        />
  );
};

export default TeamMembersPage;
